import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { Button } from './ui/button';

const ModernCarSlider = ({ images, autoPlayInterval = 4000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [images.length, isTransitioning]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [images.length, isTransitioning]);

  const goToSlide = (index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      nextSlide();
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isAutoPlaying, autoPlayInterval, nextSlide]);

  return (
    <div className="relative w-full">
      {/* Main Slider Container */}
      <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-gray-900">
        {/* Images */}
        <div className="relative h-96 md:h-[500px] lg:h-[600px]">
          {images.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                index === currentIndex
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-105'
              }`}
              style={{
                pointerEvents: index === currentIndex ? 'auto' : 'none',
              }}
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
                width="1200"
                height="600"
                srcSet={`${image.url} 800w, ${image.url} 1200w`}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 1200px"
              />
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              
              {/* Image Info */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="max-w-3xl">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    {image.title}
                  </h3>
                  <p className="text-gray-200 text-sm md:text-base">
                    {image.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Buttons */}
        <Button
          onClick={prevSlide}
          disabled={isTransitioning}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border-0 rounded-full w-12 h-12 p-0 transition-all duration-300"
          aria-label="Vorheriges Bild"
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>
        
        <Button
          onClick={nextSlide}
          disabled={isTransitioning}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border-0 rounded-full w-12 h-12 p-0 transition-all duration-300"
          aria-label="Nächstes Bild"
        >
          <ChevronRight className="w-6 h-6" />
        </Button>

        {/* Play/Pause Button */}
        <Button
          onClick={toggleAutoPlay}
          className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border-0 rounded-full w-10 h-10 p-0 transition-all duration-300"
          aria-label={isAutoPlaying ? 'Pause' : 'Play'}
        >
          {isAutoPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </Button>

        {/* Slide Counter */}
        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Dot Indicators */}
      <div className="flex justify-center mt-6 gap-3 flex-wrap">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            disabled={isTransitioning}
            className={`transition-all duration-300 rounded-full p-3 ${
              index === currentIndex
                ? 'w-12 h-12 bg-yellow-500'
                : 'w-12 h-12 bg-gray-600 hover:bg-gray-500'
            }`}
            aria-label={`Gehe zu Bild ${index + 1}`}
            style={{ minWidth: '48px', minHeight: '48px' }}
          />
        ))}
      </div>

      {/* Thumbnail Preview (Optional - Mobile Hidden) */}
      <div className="hidden lg:grid grid-cols-6 gap-3 mt-6">
        {images.slice(0, 6).map((image, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            disabled={isTransitioning}
            className={`relative aspect-video rounded-lg overflow-hidden transition-all duration-300 ${
              index === currentIndex
                ? 'ring-2 ring-yellow-500 scale-105'
                : 'opacity-60 hover:opacity-100'
            }`}
          >
            <img
              src={image.url}
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ModernCarSlider;
